import {
  types as sdkTypes,
  createImageVariantConfig,
} from '../../util/sdkLoader';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  fetchCurrentUser,
  currentUserShowSuccess,
} from '../../ducks/user.duck';
import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { fetchExchangeRates } from '../../ducks/exchangeRates.duck';
import {
  getUserAchievements,
  updateAlgoliaData,
  getStoreBlogsByUserId,
  getListingBlogsByUserId,
  STORE_BLOG_STOREFRONT_VIEW,
} from '../../util/api';
import { buildStoreBannerSeoTags } from '../../util/imageSeoTags';
import { updateCloudinaryContext } from '../../util/api';
import { fetchStoreBlogsAnalyticsSuccess } from '../StoreBlogPage/StoreBlogPage.duck';
import { generateAchievementReviews } from '../../util/reviews';
import { getAlgoliaIndexNameForLang } from '../../util/urlHelpers';
import { monthIdString, getStartOf } from '../../util/dates';

const { UUID } = sdkTypes;

export const SET_INITIAL_STATE = 'app/StoreProfilePage/SET_INITIAL_STATE';

export const SHOW_USER_REQUEST = 'app/StoreProfilePage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/StoreProfilePage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/StoreProfilePage/SHOW_USER_ERROR';

export const QUERY_LISTINGS_REQUEST =
  'app/StoreProfilePage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS =
  'app/StoreProfilePage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/StoreoPfilePage/QUERY_LISTINGS_ERROR';

export const QUERY_REVIEWS_REQUEST =
  'app/StoreProfilePage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS =
  'app/StoreProfilePage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/StoreProfilePage/QUERY_REVIEWS_ERROR';

// Reviews Received (store as provider)
export const QUERY_REVIEWS_RECEIVED_REQUEST =
  'app/StoreProfilePage/QUERY_REVIEWS_RECEIVED_REQUEST';
export const QUERY_REVIEWS_RECEIVED_SUCCESS =
  'app/StoreProfilePage/QUERY_REVIEWS_RECEIVED_SUCCESS';
export const QUERY_REVIEWS_RECEIVED_ERROR =
  'app/StoreProfilePage/QUERY_REVIEWS_RECEIVED_ERROR';

// Reviews Given (store as customer)
export const QUERY_REVIEWS_GIVEN_REQUEST =
  'app/StoreProfilePage/QUERY_REVIEWS_GIVEN_REQUEST';
export const QUERY_REVIEWS_GIVEN_SUCCESS =
  'app/StoreProfilePage/QUERY_REVIEWS_GIVEN_SUCCESS';
export const QUERY_REVIEWS_GIVEN_ERROR =
  'app/StoreProfilePage/QUERY_REVIEWS_GIVEN_ERROR';

export const UPDATE_ORDER_ERROR = 'app/StoreProfilePage/UPDATE_ORDER_ERROR';

export const FETCH_TIME_SLOTS_REQUEST =
  'app/StoreProfilePage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS =
  'app/StoreProfilePage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR =
  'app/StoreProfilePage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_USER_ACHIEVEMENTS_REQUEST =
  'app/StoreProfilePage/FETCH_USER_ACHIEVEMENTS_REQUEST';
export const FETCH_USER_ACHIEVEMENTS_SUCCESS =
  'app/StoreProfilePage/FETCH_USER_ACHIEVEMENTS_SUCCESS';
export const FETCH_USER_ACHIEVEMENTS_ERROR =
  'app/StoreProfilePage/FETCH_USER_ACHIEVEMENTS_ERROR';

export const FETCH_STORE_BLOGS_REQUEST =
  'app/StoreProfilePage/FETCH_STORE_BLOGS_REQUEST';
export const FETCH_STORE_BLOGS_SUCCESS =
  'app/StoreProfilePage/FETCH_STORE_BLOGS_SUCCESS';
export const FETCH_STORE_BLOGS_ERROR =
  'app/StoreProfilePage/FETCH_STORE_BLOGS_ERROR';

const initialState = {
  userId: null,
  userShowError: null,
  userShowInProgress: false,
  reviews: [],
  totalReviews: 0,
  achievementReviews: [],
  queryReviewsError: null,
  queryReviewsInProgress: false,
  // Reviews Received (store as provider)
  reviewsReceived: [],
  totalReviewsReceived: 0,
  reviewsReceivedInProgress: false,
  reviewsReceivedError: null,
  reviewsReceivedPagination: { page: 1, perPage: 10, totalPages: 0 },
  // Reviews Given (store as customer)
  reviewsGiven: [],
  totalReviewsGiven: 0,
  reviewsGivenInProgress: false,
  reviewsGivenError: null,
  reviewsGivenPagination: { page: 1, perPage: 10, totalPages: 0 },
  userListingRefs: [],
  totalListings: 0,
  queryListingsError: null,
  updateOrderError: null,
  isVerified: false,
  monthlyTimeSlotsByListing: {}, // { [listingId]: { [monthId]: { timeSlots, fetchTimeSlotsInProgress, fetchTimeSlotsError } } }
  fetchUserAchievementsInProgress: false,
  fetchUserAchievementsError: null,
  userAchievements: null, // { emailVerified, stripeConnected, publishedListings, displayName }
  storeBlogs: [],
  fetchStoreBlogsInProgress: false,
  fetchStoreBlogsError: null,
  /** True after a fetch attempt finished (success or error). Empty [] is a valid result — avoids refetch loops. */
  storeBlogsFetchCompleted: false,
  /** True when the server held back posts past its per-kind cap (STORE_BLOG_LIST_LIMIT). */
  storeBlogsTruncated: false,
};

export default function storeProfilePageReducer(
  state = initialState,
  action = {}
) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };
    case SHOW_USER_REQUEST:
      return {
        ...state,
        userShowInProgress: true,
        userShowError: null,
        userId: payload.userId,
      };
    case SHOW_USER_SUCCESS:
      return { ...state, userShowInProgress: false };
    case SHOW_USER_ERROR:
      return { ...state, userShowInProgress: false, userShowError: payload };
    case QUERY_REVIEWS_REQUEST:
      return {
        ...state,
        queryReviewsError: null,
        queryReviewsInProgress: true,
      };
    case QUERY_REVIEWS_SUCCESS:
      return {
        ...state,
        reviews: payload.reviews,
        totalReviews: payload.totalReviews,
        queryReviewsInProgress: false,
      };
    case QUERY_REVIEWS_ERROR:
      return {
        ...state,
        reviews: [],
        queryReviewsError: payload,
        queryReviewsInProgress: false,
      };
    case QUERY_REVIEWS_RECEIVED_REQUEST:
      return {
        ...state,
        reviewsReceivedInProgress: true,
        reviewsReceivedError: null,
      };
    case QUERY_REVIEWS_RECEIVED_SUCCESS:
      return {
        ...state,
        reviewsReceivedInProgress: false,
        reviewsReceived: payload.reviews,
        totalReviewsReceived: payload.totalReviews,
        reviewsReceivedPagination: payload.pagination,
      };
    case QUERY_REVIEWS_RECEIVED_ERROR:
      return {
        ...state,
        reviewsReceivedInProgress: false,
        reviewsReceived: [],
        reviewsReceivedError: payload,
      };
    case QUERY_REVIEWS_GIVEN_REQUEST:
      return {
        ...state,
        reviewsGivenInProgress: true,
        reviewsGivenError: null,
      };
    case QUERY_REVIEWS_GIVEN_SUCCESS:
      return {
        ...state,
        reviewsGivenInProgress: false,
        reviewsGiven: payload.reviews,
        totalReviewsGiven: payload.totalReviews,
        reviewsGivenPagination: payload.pagination,
      };
    case QUERY_REVIEWS_GIVEN_ERROR:
      return {
        ...state,
        reviewsGivenInProgress: false,
        reviewsGiven: [],
        reviewsGivenError: payload,
      };
    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,

        // Empty listings only when user id changes
        userListingRefs:
          payload.userId === state.userId ? state.userListingRefs : [],

        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return {
        ...state,
        userListingRefs: payload.listingRefs,
        totalListings: payload.totalListings,
      };
    case QUERY_LISTINGS_ERROR:
      return { ...state, userListingRefs: [], queryListingsError: payload };
    case UPDATE_ORDER_ERROR:
      return { ...state, updateOrderError: payload };
    case FETCH_TIME_SLOTS_REQUEST: {
      const { listingId, monthId } = payload;
      const listingSlots = state.monthlyTimeSlotsByListing[listingId] || {};
      return {
        ...state,
        monthlyTimeSlotsByListing: {
          ...state.monthlyTimeSlotsByListing,
          [listingId]: {
            ...listingSlots,
            [monthId]: {
              ...listingSlots[monthId],
              fetchTimeSlotsInProgress: true,
              fetchTimeSlotsError: null,
            },
          },
        },
      };
    }
    case FETCH_TIME_SLOTS_SUCCESS: {
      const { listingId, monthId, timeSlots } = payload;
      const listingSlots = state.monthlyTimeSlotsByListing[listingId] || {};
      return {
        ...state,
        monthlyTimeSlotsByListing: {
          ...state.monthlyTimeSlotsByListing,
          [listingId]: {
            ...listingSlots,
            [monthId]: {
              ...listingSlots[monthId],
              fetchTimeSlotsInProgress: false,
              timeSlots,
            },
          },
        },
      };
    }
    case FETCH_TIME_SLOTS_ERROR: {
      const { listingId, monthId, error } = payload;
      const listingSlots = state.monthlyTimeSlotsByListing[listingId] || {};
      return {
        ...state,
        monthlyTimeSlotsByListing: {
          ...state.monthlyTimeSlotsByListing,
          [listingId]: {
            ...listingSlots,
            [monthId]: {
              ...listingSlots[monthId],
              fetchTimeSlotsInProgress: false,
              fetchTimeSlotsError: error,
            },
          },
        },
      };
    }
    case FETCH_USER_ACHIEVEMENTS_REQUEST:
      return {
        ...state,
        fetchUserAchievementsInProgress: true,
        fetchUserAchievementsError: null,
      };
    case FETCH_USER_ACHIEVEMENTS_SUCCESS:
      return {
        ...state,
        fetchUserAchievementsInProgress: false,
        userAchievements: payload.achievements,
        isVerified: payload.achievements.emailVerified,
        totalListings: payload.achievements.publishedListings,
        achievementReviews: payload.achievementReviews,
        // Merge achievement reviews with existing reviews
        reviews: [...state.reviews],
        totalReviews: state.totalReviews ,
      };
    case FETCH_USER_ACHIEVEMENTS_ERROR:
      return {
        ...state,
        fetchUserAchievementsInProgress: false,
        fetchUserAchievementsError: payload,
      };
    case FETCH_STORE_BLOGS_REQUEST:
      return {
        ...state,
        fetchStoreBlogsInProgress: true,
        fetchStoreBlogsError: null,
      };
    case FETCH_STORE_BLOGS_SUCCESS:
      return {
        ...state,
        fetchStoreBlogsInProgress: false,
        storeBlogs: payload,
        storeBlogsFetchCompleted: true,
        storeBlogsTruncated: !!action.meta?.truncated,
      };
    case FETCH_STORE_BLOGS_ERROR:
      return {
        ...state,
        fetchStoreBlogsInProgress: false,
        fetchStoreBlogsError: payload,
        storeBlogsFetchCompleted: true,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showUserRequest = userId => ({
  type: SHOW_USER_REQUEST,
  payload: { userId },
});

export const showUserSuccess = () => ({
  type: SHOW_USER_SUCCESS,
});

export const showUserError = e => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = ({ reviews, totalReviews }) => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: { reviews, totalReviews },
});

export const queryReviewsError = e => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsReceivedRequest = () => ({
  type: QUERY_REVIEWS_RECEIVED_REQUEST,
});

export const queryReviewsReceivedSuccess = ({
  reviews,
  totalReviews,
  pagination,
}) => ({
  type: QUERY_REVIEWS_RECEIVED_SUCCESS,
  payload: { reviews, totalReviews, pagination },
});

export const queryReviewsReceivedError = e => ({
  type: QUERY_REVIEWS_RECEIVED_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsGivenRequest = () => ({
  type: QUERY_REVIEWS_GIVEN_REQUEST,
});

export const queryReviewsGivenSuccess = ({
  reviews,
  totalReviews,
  pagination,
}) => ({
  type: QUERY_REVIEWS_GIVEN_SUCCESS,
  payload: { reviews, totalReviews, pagination },
});

export const queryReviewsGivenError = e => ({
  type: QUERY_REVIEWS_GIVEN_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = userId => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: { userId },
});

export const queryListingsSuccess = ({ listingRefs, totalListings }) => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { listingRefs, totalListings },
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const updateOrderError = e => ({
  type: UPDATE_ORDER_ERROR,
  error: true,
  payload: e,
});

export const fetchTimeSlotsRequest = (listingId, monthId) => ({
  type: FETCH_TIME_SLOTS_REQUEST,
  payload: { listingId, monthId },
});

export const fetchTimeSlotsSuccess = (listingId, monthId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: { listingId, monthId, timeSlots },
});

export const fetchTimeSlotsError = (listingId, monthId, error) => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: { listingId, monthId, error },
});

export const getStoreBlogs = state => state.StoreProfilePage.storeBlogs;

export const getFetchStoreBlogsInProgress = state =>
  state.StoreProfilePage.fetchStoreBlogsInProgress;

export const getFetchStoreBlogsError = state =>
  state.StoreProfilePage.fetchStoreBlogsError;

export const getStoreBlogsTruncated = state => state.StoreProfilePage.storeBlogsTruncated;

export const getStoreBlogsFetchCompleted = state =>
  state.StoreProfilePage.storeBlogsFetchCompleted;

export const fetchUserAchievementsRequest = () => ({
  type: FETCH_USER_ACHIEVEMENTS_REQUEST,
});

export const fetchUserAchievementsSuccess = ({
  achievements,
  achievementReviews,
}) => ({
  type: FETCH_USER_ACHIEVEMENTS_SUCCESS,
  payload: { achievements, achievementReviews },
});

export const fetchUserAchievementsError = e => ({
  type: FETCH_USER_ACHIEVEMENTS_ERROR,
  error: true,
  payload: e,
});

export const fetchStoreBlogsRequest = () => ({
  type: FETCH_STORE_BLOGS_REQUEST,
});

export const fetchStoreBlogsSuccess = (blogs, { truncated = false } = {}) => ({
  type: FETCH_STORE_BLOGS_SUCCESS,
  payload: blogs,
  meta: { truncated },
});

export const fetchStoreBlogsError = e => ({
  type: FETCH_STORE_BLOGS_ERROR,
  error: true,
  payload: e,
});

export const queryUserListings = (userId, config) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(queryListingsRequest(userId));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      author_id: userId,
      include: ['author', 'images'],
      'fields.image': [
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    })
    .then(response => {
      // Pick only the id and type properties from the response listings
      const totalListings = response.data.meta.totalItems;
      const listingRefs = response.data.data.map(({ id, type }) => ({
        id,
        type,
      }));
      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess({ listingRefs, totalListings }));
      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

export const queryUserReviews = userId => (dispatch, getState, sdk) => {
  dispatch(queryReviewsRequest());
  const userIdObj = userId?.uuid ? userId : new UUID(userId);
  return sdk.reviews
    .query({
      subjectId: userIdObj,
      type: 'ofProvider',
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const totalReviews = response.data.meta.totalItems;
      const reviews = denormalisedResponseEntities(response);

      dispatch(
        queryReviewsSuccess({
          reviews,
          totalReviews,
        })
      );
      return response;
    })
    .catch(e => {
      dispatch(queryReviewsError(e));
      throw e;
    });
};

/** Page size for store profile reviews — keep small to avoid serializing 100 reviewer avatars on SSR. */
export const STORE_PROFILE_REVIEWS_PER_PAGE = 15;

// Query reviews where store is provider (reviews received)
export const queryReviewsReceived = (userId, page = 1, perPage = 10) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(queryReviewsReceivedRequest());

  // Ensure userId is a UUID object
  const userIdObj = userId?.uuid ? userId : new UUID(userId);

  return sdk.reviews
    .query({
      // Reviews received by store owner = reviews where owner is subject of provider-review.
      subjectId: userIdObj,
      type: 'ofProvider',
      state: 'public',
      include: ['author', 'author.profileImage', 'listing'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
      page,
      perPage: perPage,
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      const meta = response.data.meta;

      // If page > 1, append to existing reviews, otherwise replace
      const state = getState().StoreProfilePage;
      const existingReviews = page > 1 ? state.reviewsReceived || [] : [];
      const allReviews = page > 1 ? [...existingReviews, ...reviews] : reviews;

      dispatch(
        queryReviewsReceivedSuccess({
          reviews: [...allReviews],
          totalReviews: meta.totalItems,
          pagination: {
            page: meta.page,
            perPage: meta.perPage,
            totalPages: meta.totalPages,
          },
        })
      );
      return response;
    })
    .catch(e => dispatch(queryReviewsReceivedError(storableError(e))));
};

// Query reviews where store is customer (reviews given)
export const queryReviewsGiven = (userId, page = 1, perPage = 10) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(queryReviewsGivenRequest());

  // Ensure userId is a UUID object
  const userIdObj = userId?.uuid ? userId : new UUID(userId);

  return sdk.reviews
    .query({
      subjectId: userIdObj,
      type: 'ofCustomer',
      include: ['author', 'author.profileImage', 'listing'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
      page,
      perPage: perPage,
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      const meta = response.data.meta;

      // If page > 1, append to existing reviews, otherwise replace
      const state = getState().StoreProfilePage;
      const existingReviews = page > 1 ? state.reviewsGiven || [] : [];
      const allReviews = page > 1 ? [...existingReviews, ...reviews] : reviews;

      dispatch(
        queryReviewsGivenSuccess({
          reviews: allReviews,
          totalReviews: meta.totalItems,
          pagination: {
            page: meta.page,
            perPage: meta.perPage,
            totalPages: meta.totalPages,
          },
        })
      );
      return response;
    })
    .catch(e => {
      dispatch(queryReviewsGivenError(storableError(e)));
    });
};

// ================ Thunks ================ //

export const fetchUserAchievements = userId => (dispatch, getState, sdk) => {
  dispatch(fetchUserAchievementsRequest());

  return getUserAchievements(userId.uuid)
    .then(({ data }) => {
      const {
        emailVerified,
        stripeConnected,
        publishedListings,
        displayName,
      } = data;

      const achievementReviews = generateAchievementReviews(
        emailVerified,
        stripeConnected,
        publishedListings,
        displayName
      );

      dispatch(
        fetchUserAchievementsSuccess({
          achievements: {
            emailVerified,
            stripeConnected,
            publishedListings,
            displayName,
          },
          achievementReviews,
        })
      );
    })
    .catch(e => {
      dispatch(fetchUserAchievementsError(storableError(e)));
    });
};

export const fetchStoreBlogsData = userId => (dispatch, _getState, sdk) => {
  dispatch(fetchStoreBlogsRequest());

  try {
    if (!userId) {
      dispatch(fetchStoreBlogsSuccess([]));
      return Promise.resolve([]);
    }

    return Promise.allSettled([
      getStoreBlogsByUserId(userId, { view: STORE_BLOG_STOREFRONT_VIEW }),
      getListingBlogsByUserId(userId, { view: STORE_BLOG_STOREFRONT_VIEW }),
    ])
      .then(([storeBlogsResult, listingBlogsResult]) => {
        const storeBlogsResponse =
          storeBlogsResult.status === 'fulfilled'
            ? storeBlogsResult.value
            : null;
        const listingBlogsResponse =
          listingBlogsResult.status === 'fulfilled'
            ? listingBlogsResult.value
            : null;

        const { blogs: storeBlogs, analytics } = storeBlogsResponse?.data || {
          blogs: [],
          analytics: null,
        };
        const { blogs: listingBlogs } = listingBlogsResponse?.data || {
          blogs: [],
        };

        // A read that failed means this store page shows no blogs. It does not mean the page
        // failed (ticket 91).
        //
        // Why this is not console.error with the reason attached: when the read times out or
        // is answered with an error page rather than JSON, the reason is a parse error
        // complaining about '<', which is what production was logging. It reads like a code
        // fault and says nothing useful. One line naming which read failed and why is enough,
        // and the page carries on.
        const describe = reason =>
          reason?.name === 'ApiTimeoutError'
            ? 'took too long'
            : reason?.name === 'SyntaxError'
            ? 'answered with something that was not JSON'
            : reason?.message || 'failed';

        if (storeBlogsResult.status === 'rejected') {
          console.warn(`Store blogs unavailable: ${describe(storeBlogsResult.reason)}.`);
        }

        if (listingBlogsResult.status === 'rejected') {
          console.warn(`Listing blogs unavailable: ${describe(listingBlogsResult.reason)}.`);
        }

        const mergedBlogsMap = {};
        [...(storeBlogs || []), ...(listingBlogs || [])].forEach(blog => {
          const key = blog?._id || blog?.id;
          if (key) {
            mergedBlogsMap[key] = blog;
          }
        });

        const mergedBlogs = Object.values(mergedBlogsMap);
        const truncated =
          storeBlogsResponse?.data?.truncated === true ||
          listingBlogsResponse?.data?.truncated === true;
        dispatch(fetchStoreBlogsSuccess(mergedBlogs, { truncated }));

        if (analytics) {
          dispatch(fetchStoreBlogsAnalyticsSuccess(analytics));
        }

        return mergedBlogs;
      })
      .catch(error => {
        console.error('Error fetching store blogs:', error);
        const storableErr = storableError(error);
        dispatch(fetchStoreBlogsError(storableErr));
        return [];
      });
  } catch (error) {
    console.error('Error in fetchStoreBlogsData:', error);
    const storableErr = storableError(error);
    dispatch(fetchStoreBlogsError(storableErr));
    return Promise.resolve([]);
  }
};

/**
 * Fetch a user and merge into marketplace entities.
 * @param {UUID} userId - User to fetch
 * @param {Object} options - Optional: { silent: true } to skip showUserRequest (no loading state)
 */
export const showUser = (userId, options = {}) => (dispatch, getState, sdk) => {
  const { silent = false } = options;
  if (!silent) {
    dispatch(showUserRequest(userId));
  }
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.user': [
        'createdAt',
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.bio',
        'profile.publicData',
        'profile.metadata',
        'profile.protectedData',
      ],
      'fields.image': [
        'variants.square-small',
        'variants.square-small2x',

        // Social media
        'variants.facebook',
        'variants.twitter',

        'variants.scaled-small',
      ],
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(showUserSuccess());
      return response;
    })
    .catch(e => {
      if (!silent) {
        dispatch(showUserError(storableError(e)));
      }
      throw e;
    });
};

export const updateRecordsOrder = data => (dispatch, getState, sdk) => {
  // Update records order to Flex Promises
  const updateRecordsToFlex = data.map(d =>
    sdk.ownListings.update({
      id: d.objectID,
      publicData: {
        order: d.order,
      },
    })
  );

  // Update records order to Algolia Promise
  const updateRecordsToAlgolia = updateAlgoliaData({
    indexName: getAlgoliaIndexNameForLang(
      (typeof window !== 'undefined' && localStorage.getItem('appLanguage')) ||
      'en'
    ),
    data,
  });

  Promise.all([...updateRecordsToFlex, updateRecordsToAlgolia]).catch(e => {
    dispatch(updateOrderError(storableError(e)));
    setTimeout(() => dispatch(updateOrderError(null)), 3000);
  });
};

/**
 * Reorder store media (showcase gallery) and persist to current user profile.
 * Owner-only: uses sdk.currentUser.updateProfile. Refreshes profile via silent
 * showUser so the UI updates without a full-page loading state.
 */
export const reorderStoreMedia = orderedMedia => (dispatch, getState, sdk) => {
  const state = getState();
  const currentUser = state.user?.currentUser;
  const profileUserId = state.StoreProfilePage?.userId;

  if (!currentUser?.id) {
    return Promise.reject(new Error('Not authenticated'));
  }

  const existingPublicData = currentUser.attributes?.profile?.publicData || {};
  const publicData = {
    storeMedia: orderedMedia,
    storeMediaMeta: existingPublicData.storeMediaMeta || {
      totalBytes: 0,
      lastUpdated: new Date().toISOString(),
    },
  };

  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.currentUser
    .updateProfile({ publicData }, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      const updatedUser = entities?.[0];
      if (updatedUser) {
        dispatch(currentUserShowSuccess(updatedUser));
      }
      // Silent refresh: update profile user in marketplace entities without
      // dispatching showUserRequest, so the page does not show loading state.
      if (profileUserId) {
        return dispatch(showUser(profileUserId, { silent: true }))
          .then(() => response)
          .catch(() => response); // Reorder already saved; return success even if refresh fails
      }
      return response;
    });
};

/**
 * Update store cover fields and refresh profile user entity for immediate UI sync.
 */
export const updateStoreCover = payload => (dispatch, getState, sdk) => {
  const state = getState();
  const currentUser = state.user?.currentUser;
  const profileUserId = state.StoreProfilePage?.userId;

  if (!currentUser?.id) {
    return Promise.reject(new Error('Not authenticated'));
  }

  // Attach seoTags to storeCoverImage when a new image is being saved.
  // Color-only updates (no public_id) pass through unchanged.
  const enrichedPayload = { ...payload };
  if (payload.storeCoverImage?.public_id) {
    const { storeName = '', city = '', storeAddress } =
      currentUser.attributes?.profile?.publicData || {};
    const resolvedCity = storeAddress?.city || city;
    const seoTags = buildStoreBannerSeoTags(storeName, resolvedCity);
    enrichedPayload.storeCoverImage = { ...payload.storeCoverImage, seoTags };
    updateCloudinaryContext({
      publicId: payload.storeCoverImage.public_id,
      context: seoTags,
      resourceType: 'image',
    }).catch(err => console.error('Cloudinary banner context update failed:', err));
  }

  const publicData = { ...enrichedPayload };

  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.currentUser
    .updateProfile({ publicData }, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      const updatedUser = entities?.[0];
      if (updatedUser) {
        dispatch(currentUserShowSuccess(updatedUser));
      }
      if (profileUserId) {
        return dispatch(showUser(profileUserId, { silent: true }))
          .then(() => response)
          .catch(() => response);
      }
      return response;
    });
};

const shouldLoadReviews = params => params?.tab === 'reviews';

export const loadData = (params, _search, _config) => (
  dispatch,
  getState,
  sdk
) => {
  const userId = new UUID(params.id);
  const state = getState().StoreProfilePage;
  const currentUserId = state.userId;

  // If user is already loaded and ID hasn't changed, skip reload.
  // This prevents reloading when only query params (filters) change,
  // avoiding the "Loading Profile" screen on filter updates.
  if (
    currentUserId &&
    currentUserId.uuid === userId.uuid &&
    !state.userShowInProgress &&
    !state.userShowError
  ) {
    const doLoadReviews = shouldLoadReviews(params);

    // User already loaded, just ensure exchange rates are fetched if needed
    const { exchangeRates } = getState().exchangeRate;
    if (
      typeof window !== 'undefined' &&
      exchangeRates &&
      exchangeRates.length === 0
    ) {
      dispatch(fetchExchangeRates());
    }

    const pendingFetches = [];

    // Always fetch blogs (needed for schema on all tabs during SSR)
    if (
      !state.fetchStoreBlogsInProgress &&
      !state.storeBlogsFetchCompleted
    ) {
      pendingFetches.push(dispatch(fetchStoreBlogsData(userId.uuid)));
    }

    if (doLoadReviews) {
      const achievementsLoaded = !!state.userAchievements;

      const receivedLoaded =
        Array.isArray(state.reviewsReceived) &&
        state.reviewsReceived.length > 0 &&
        state.reviewsReceivedPagination?.page === 1;
      const givenLoaded =
        Array.isArray(state.reviewsGiven) &&
        state.reviewsGiven.length > 0 &&
        state.reviewsGivenPagination?.page === 1;

      if (!receivedLoaded && !state.reviewsReceivedInProgress) {
        pendingFetches.push(dispatch(queryReviewsReceived(userId, 1, 10)));
      }
      if (!givenLoaded && !state.reviewsGivenInProgress) {
        pendingFetches.push(dispatch(queryReviewsGiven(userId, 1, 10)));
      }
      if (!achievementsLoaded && !state.fetchUserAchievementsInProgress) {
        pendingFetches.push(dispatch(fetchUserAchievements(userId)));
      }
    }

    return pendingFetches.length ? Promise.all(pendingFetches) : Promise.resolve();
  }

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  const doLoadReviews = shouldLoadReviews(params);

  // Fetch blogs in parallel with user/reviews so SSR has blog data
  // for hasBlogPosts and blogCollectionSchema without a sequential waterfall.
  // Blog failure must never block SSR — catch and fall back to empty.
  const blogPromise = dispatch(fetchStoreBlogsData(userId.uuid)).catch(() => {});

  return Promise.all([
    dispatch(showUser(userId)),
    dispatch(queryReviewsReceived(userId, 1, STORE_PROFILE_REVIEWS_PER_PAGE)),
    blogPromise,
  ]).then(response => {
    let { currentUser } = getState().user;

    if (!currentUser || (currentUser && !currentUser.id)) {
      currentUser = dispatch(fetchCurrentUser());
    }

    // Fetch exchange rates, do not fetch if already exists.
    const { exchangeRates } = getState().exchangeRate;

    if (
      typeof window != 'undefined' &&
      exchangeRates &&
      exchangeRates.length == 0
    ) {
      dispatch(fetchExchangeRates());
    }

    const postLoadFetches = [];

    if (doLoadReviews) {
      // queryReviewsReceived page 1 is already fetched above — only fetch
      // reviewsGiven and achievements here (both needed only on the reviews tab).
      postLoadFetches.push(
        dispatch(queryReviewsGiven(userId, 1, 10)),
        dispatch(fetchUserAchievements(userId))
      );
    }

    return postLoadFetches.length
      ? Promise.all(postLoadFetches).then(() => response)
      : response;
  });
};

// ================ Time Slots Thunks ================ //

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = (listingId, start, end, timeZone) => (
  dispatch,
  getState,
  sdk
) => {
  const monthId = monthIdString(start, timeZone);

  dispatch(fetchTimeSlotsRequest(listingId, monthId));

  // The maximum pagination page size for timeSlots is 500
  const extraParams = {
    perPage: 500,
    page: 1,
  };

  return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
    .then(timeSlots => {
      dispatch(fetchTimeSlotsSuccess(listingId, monthId, timeSlots));
      return timeSlots;
    })
    .catch(e => {
      dispatch(fetchTimeSlotsError(listingId, monthId, storableError(e)));
      throw e;
    });
};
